import { forwardRef } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

/**
 * Closes a ballot in a state Ballotpedia's local coverage does not reach (the
 * "Some local races not available" chip's states), saying what may be missing and
 * where to confirm the full ballot. The chip scrolls here, so it takes focus.
 */
const LocalRacesNotice = forwardRef<HTMLElement, { onCheckElectionOffice: () => void }>(
  function LocalRacesNotice({ onCheckElectionOffice }, ref) {
    const { t } = useTranslation('ballot');
    return (
      <Box
        ref={ref}
        component="section"
        tabIndex={-1}
        aria-labelledby="local-races-notice-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mx: 1,
          mt: 1,
          px: 1.5,
          py: 1.25,
          // The Voting Info section headers' aqua and text color.
          backgroundColor: semanticColors.themeAqua,
          borderRadius: BORDER_RADIUS,
          '&:focus-visible': {
            outline: `2px solid ${semanticColors.brandAccent}`,
            outlineOffset: '2px',
          },
        }}
      >
        <InfoOutlinedIcon
          fontSize="small"
          aria-hidden="true"
          sx={{ color: semanticColors.textPrimary, mt: '1px' }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            id="local-races-notice-title"
            component="h2"
            variant="body2"
            sx={{ fontWeight: 600, color: semanticColors.textPrimary }}
          >
            {t('votingInfo.localRaces.text')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: semanticColors.textPrimary, lineHeight: 1.45 }}
          >
            {t('elections.localRacesNoticeBody')}
          </Typography>
          {/* Same link and wording as the Voting Info local-races notice. */}
          <Typography
            variant="caption"
            sx={{ color: semanticColors.textPrimary, lineHeight: 1.45, mt: 0.5 }}
          >
            <Link
              component="button"
              type="button"
              onClick={onCheckElectionOffice}
              sx={{
                color: semanticColors.textPrimary,
                fontWeight: 600,
                textAlign: 'start',
                verticalAlign: 'baseline',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                '&:focus-visible': {
                  outline: `2px solid ${semanticColors.brandAccent}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {t('votingInfo.localRaces.checkLink')}
            </Link>{' '}
            {t('votingInfo.localRaces.textSuffix')}
          </Typography>
        </Box>
      </Box>
    );
  }
);

export default LocalRacesNotice;
